<script setup>
const props = defineProps({
    title: {
        type: String,
        required: false,
    },
    description: {
        type: String,
        required: false,
    },
    label: {
        type: String,
        default: 'Share',
    },
    link: {
        type: String,
        required: false,
    },
    copyToClipboard: {
        type: Boolean,
        default: false,
    },
});

const showToast = ref(false);

const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(props.title)}&url=${props.link ? props.link : window.location.href}`;
    window.open(url, '_blank');
};

const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${props.link ? props.link : window.location.href}`;
    window.open(url, '_blank');
};

const shareLinkedIn = () => {
    // LinkedIn retired the shareArticle title/summary params in 2021 — it now
    // pulls the title/description/image from the shared page's Open Graph tags.
    // share-offsite is the current supported endpoint; it takes only the url and
    // scrapes the rest (the shared-schedule page sets ogTitle/ogDescription/ogImage).
    const target = props.link ? props.link : window.location.href;
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(target)}`;
    window.open(url, '_blank');
};

const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(props.link ? props.link : window.location.href).then(() => {
        showToast.value = true;
        setTimeout(() => {
            showToast.value = false;
        }, 2000);
    });
};
</script>

<template>
    <div class="share flex items-center gap-2 font-normal uppercase text-(--magenta)">
        <p class="font-hubspot-sans font-medium text-[12px] tracking-[0.84px] m-0 mr-1">{{ label }}</p>
        <ul class="social-links flex" style="gap: 16px;">
            <li>
                <button @click="shareLinkedIn" aria-label="Share on LinkedIn">
                    <MessSvg type="linkedIn" />
                    <span class="sr-only">(opens in a new tab)</span>
                </button>
            </li>
            <li>
                <button @click="shareFacebook" aria-label="Share on Facebook">
                    <MessSvg type="facebook" />
                    <span class="sr-only">(opens in a new tab)</span>
                </button>
            </li>
            <li>
                <button @click="shareTwitter" aria-label="Share on Twitter">
                    <MessSvg type="twitter" />
                    <span class="sr-only">(opens in a new tab)</span>
                </button>
            </li>
            <li v-if="copyToClipboard" class="copy-link relative">
                <button @click="copyLinkToClipboard" aria-label="Copy link to clipboard">
                    <MessSvg type="clipboard" />
                </button>
                <div v-if="showToast" class="toast">Link copied to clipboard!</div>
            </li>
        </ul>
    </div>
</template>

<style>
.share .social-links li {
    cursor: pointer;
    color: var(--magenta);
}

.share .social-links li svg {
    transition: 100ms ease;
    width: 2.25rem;
    height: 2.25rem;
}

.share .social-links li svg circle {
    stroke: var(--magenta);
}

.share .social-links li svg path {
    fill: var(--magenta);
}

.share .social-links li:hover {
    color: var(--dark-pink);
}

.share .social-links li:hover svg path {
    fill: white;
}

.share .social-links li:hover svg circle {
    fill: var(--magenta);
    stroke: var(--magenta);
}

.share .social-links .copy-link .toast {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--orange-dark);
    color: white;
    padding: 0.5rem 1rem;
    font-size: 14px;
    font-weight: normal;
    text-transform: none;
    opacity: 0;
    animation: fadeInOut 2s ease-in-out;
    white-space: nowrap;
    margin-bottom: 8px;
}

@keyframes fadeInOut {
    0% {
        opacity: 0;
        transform: translate(-50%, 10px);
    }

    10% {
        opacity: 1;
        transform: translate(-50%, 0);
    }

    90% {
        opacity: 1;
        transform: translate(-50%, 0);
    }

    100% {
        opacity: 0;
        transform: translate(-50%, 10px);
    }
}
</style>
